import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Footer } from "@/components/Footer";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Normalize email to lowercase before sending
      const user = await login(formData.email.toLowerCase().trim(), formData.password);
      toast.success("Welcome back!");
      
      // Redirect based on user type
      // Experts → Expert Dashboard
      // Regular users → Homepage (to browse experts)
      if (user.is_expert) {
        navigate("/expert-dashboard");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      <div className="flex-1 flex">
        {/* Left Side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <motion.div 
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link to="/" className="inline-block mb-12">
              <h1 className="font-display text-3xl text-gold-gradient">ExpertBase</h1>
            </Link>

            <h2 className="font-display text-4xl mb-2">Welcome back</h2>
            <p className="text-slate-400 mb-8">Sign in to your account to continue</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="pl-12 pr-12 bg-charcoal border-slate-800 focus:border-gold-500 h-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-semibold py-6 rounded-sm"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-obsidian text-slate-500">or continue with</span>
            </div>
          </div>

          {/* Google Sign In Button */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const redirectUrl = window.location.origin + '/dashboard';
              console.log("Google Sign-In: Redirect URL:", redirectUrl);
              console.log("Google Sign-In: Full auth URL:", `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-6 rounded-sm border-0 flex items-center justify-center gap-3"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="text-center mt-4">
            <Link 
              to="/forgot-password" 
              className="text-gold-500 hover:text-gold-400 text-sm flex items-center justify-center gap-2"
              data-testid="forgot-password-link"
            >
              <KeyRound className="w-4 h-4" />
              Forgot Password?
            </Link>
          </div>

          <p className="text-center text-slate-400 mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-gold-500 hover:text-gold-400 transition-colors">
              Create one
            </Link>
          </p>
        </motion.div>
        </div>

        {/* Right Side - Branding */}
        <div className="hidden lg:block lg:flex-1 relative bg-gradient-to-br from-charcoal to-obsidian">
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="max-w-md text-center">
              <div className="w-24 h-24 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="font-display text-4xl text-gold-500">EB</span>
              </div>
              <blockquote className="font-display text-3xl text-white mb-6">
                "One conversation changed everything for our startup."
              </blockquote>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gold-500/20 flex items-center justify-center">
                  <span className="font-semibold text-gold-500">FA</span>
                </div>
                <div className="text-left">
                  <p className="font-medium">Founder A</p>
                  <p className="text-sm text-slate-400">Tech Startup</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
